/**
 * Outil « Polynômes de Taylor » (Boîte à outils, prépa).
 *
 * Une fonction usuelle et son polynôme de Taylor en 0 à l'ordre n (curseur) :
 * coefficients exacts, courbes superposées, zone où l'écart reste sous 0,1.
 * Au doigt : f(x), Tₙ(x) et l'erreur. Les DL de ln(1 + x) et 1/(1 − x) ne
 * s'améliorent qu'entre −1 et 1, ceux de eˣ, sin et cos partout.
 */

import { useMemo, useRef, useState, type PointerEvent } from 'react';
import { MathText, cn, sampleFunctionPath } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, ParamSlider } from '../../ui/controls';
import { makeFrame } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import { FIGURE_BG_CLASS, PlotGrid } from '../../ui/svg-parts';
import {
  functionById,
  FUNCTIONS,
  goodZone,
  ORDER_MAX,
  taylorTex,
  taylorValue,
  type FunctionId,
} from './taylor';

const SIZE = 320;
const VP = { xMin: -4, xMax: 4, yMin: -3, yMax: 5 };
const frame = makeFrame(VP, SIZE);
const F_COLOR = 'var(--color-primary-500)';
const T_COLOR = 'var(--color-accent-500)';
const ZONE = 'var(--color-xp-500)';

export function TaylorTool(_props: ToolProps) {
  const [id, setId] = useState<FunctionId>('exp');
  const [n, setN] = useState(2);
  const [x0, setX0] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const fn = functionById(id);

  const fPath = useMemo(() => sampleFunctionPath(fn.f, [VP.xMin, VP.xMax], frame), [fn]);
  const tPath = useMemo(
    () => sampleFunctionPath((x) => taylorValue(fn, n, x), [VP.xMin, VP.xMax], frame),
    [fn, n],
  );
  const zone = useMemo(() => goodZone(fn, n, [VP.xMin, VP.xMax]), [fn, n]);

  const readAt = (e: PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    if (!r.width) return;
    const x = frame.ix(((e.clientX - r.left) / r.width) * SIZE);
    setX0(Math.round(x * 20) / 20);
  };

  const fx = x0 !== null ? fn.f(x0) : NaN;
  const tx = x0 !== null ? taylorValue(fn, n, x0) : NaN;
  const clip = (y: number) => Math.min(VP.yMax + 1, Math.max(VP.yMin - 1, y));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Fonction">
        {FUNCTIONS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={f.id === id}
            onClick={() => {
              setId(f.id);
              setX0(null);
            }}
            className={cn(
              'min-h-11 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
              f.id === id
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-600 hover:bg-ink-50',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <ParamSlider
        name="Ordre n"
        ariaLabel="Ordre n du polynôme de Taylor"
        value={n}
        min={0}
        max={ORDER_MAX}
        step={1}
        onChange={setN}
        color={T_COLOR}
      />

      <div
        className="border-ink-100 overflow-x-auto rounded-2xl border px-3 py-2"
        data-testid="taylor-formula"
      >
        <MathText className="text-ink-900 text-base">
          {`$\\displaystyle ${fn.tex} = ${taylorTex(fn, n)} + o\\left(x^{${n}}\\right)$`}
        </MathText>
      </div>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={`Courbe de f et de son polynôme de Taylor d’ordre ${n} en 0`}
          onPointerDown={(e) => {
            dragging.current = true;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            readAt(e);
          }}
          onPointerMove={(e) => {
            if (dragging.current) readAt(e);
          }}
          onPointerUp={() => (dragging.current = false)}
          onPointerCancel={() => (dragging.current = false)}
        >
          {zone && (
            <rect
              x={frame.tx(zone[0])}
              y={0}
              width={Math.max(2, frame.tx(zone[1]) - frame.tx(zone[0]))}
              height={SIZE}
              fill={ZONE}
              fillOpacity={0.12}
              data-testid="taylor-zone"
            />
          )}
          <PlotGrid frame={frame} step={1} />
          <path d={fPath} fill="none" stroke={F_COLOR} strokeWidth={3} />
          <path d={tPath} fill="none" stroke={T_COLOR} strokeWidth={2.5} strokeDasharray="7 5" />
          {x0 !== null && (
            <g>
              <line
                x1={frame.tx(x0)}
                y1={0}
                x2={frame.tx(x0)}
                y2={SIZE}
                stroke="var(--color-ink-400)"
                strokeWidth={1.25}
                strokeDasharray="4 4"
              />
              {Number.isFinite(fx) && (
                <circle cx={frame.tx(x0)} cy={frame.ty(clip(fx))} r={5} fill={F_COLOR} />
              )}
              {Number.isFinite(tx) && (
                <circle cx={frame.tx(x0)} cy={frame.ty(clip(tx))} r={5} fill={T_COLOR} />
              )}
            </g>
          )}
        </svg>
      </div>

      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label="Légende">
        <li className="text-ink-700 flex items-center gap-2">
          <span className="h-1 w-5 rounded" style={{ background: F_COLOR }} aria-hidden />
          <MathText>{`$f(x) = ${fn.tex}$`}</MathText>
        </li>
        <li className="text-ink-700 flex items-center gap-2">
          <span className="h-1 w-5 rounded" style={{ background: T_COLOR }} aria-hidden />
          <MathText>{`$T_{${n}}(x)$`}</MathText>
        </li>
        <li className="text-ink-700 flex items-center gap-2">
          <span className="h-3 w-5 rounded opacity-30" style={{ background: ZONE }} aria-hidden />
          écart &lt; 0,1
        </li>
      </ul>

      <p
        className="text-ink-900 min-h-12 text-base font-semibold"
        aria-live="polite"
        data-testid="taylor-reading"
      >
        {x0 === null
          ? 'Touche le graphique pour comparer f(x) et Tₙ(x).'
          : Number.isFinite(fx)
            ? `x = ${formatNumber(x0, 2)} : f(x) ≈ ${formatNumber(fx)}, T${toSub(n)}(x) ≈ ${formatNumber(tx)}, écart ≈ ${formatNumber(Math.abs(fx - tx))}`
            : `x = ${formatNumber(x0, 2)} : f n’est pas définie ici.`}
      </p>

      <Note live={false}>
        {zoneText(zone)}
        {id === 'ln1p' || id === 'geom' || id === 'sqrt1p'
          ? 'Pour x > 1, la série diverge : les premiers ordres peuvent encore rapprocher le polynôme de la courbe, mais à partir d’un certain ordre, monter n l’en éloigne.'
          : 'Monte n : la zone s’élargit, le polynôme épouse la courbe de plus en plus loin.'}
      </Note>
    </div>
  );
}

/** « pour x entre −0,9 et 0,78 », en signalant les bords de la fenêtre */
function zoneText(zone: [number, number] | null): string {
  if (!zone) return '';
  const atLeft = zone[0] <= VP.xMin + 0.02;
  const atRight = zone[1] >= VP.xMax - 0.02;
  if (atLeft && atRight) return 'Écart inférieur à 0,1 dans toute la fenêtre. ';
  const lo = atLeft ? `${formatNumber(VP.xMin)} (bord de la fenêtre)` : formatNumber(zone[0], 2);
  const hi = atRight ? `${formatNumber(VP.xMax)} (bord de la fenêtre)` : formatNumber(zone[1], 2);
  return `Écart inférieur à 0,1 pour x entre ${lo} et ${hi}. `;
}

const SUB = '₀₁₂₃₄₅₆₇₈₉';
function toSub(k: number): string {
  return String(k)
    .split('')
    .map((c) => SUB[Number(c)])
    .join('');
}
